import { EventEmitter } from 'events'
import { CBORSocket } from 'cocom'
import { toBase64 } from '../util/toBase64.js'

export default class SocketManager extends EventEmitter {
  #subscriptions = new Set()

  #headers = {}

  #socket

  #delegate

  constructor (delegate) {
    super()
    this.#delegate = delegate
    this.setMaxListeners(10000)
  }

  setHeader = (key, value) => {
    this.#headers[key] = value
  }

  connect = async () => {
    // create the socket if not already created, the function passed in to
    // the socket consturctor will always use the latest auth token saved on
    // the instance on reconnects
    if (!this.#socket) {
      this.#socket = new CBORSocket({
        getUrl: async () => this.#generateUrl(await this.#delegate.getSocketAuth()),
        getProtocols: async () => this.#generateProtocols(await this.#delegate.getSocketAuth())
      })
      this.#socket.setMaxReconnectDelay(10 * 60 * 1000)
      this.#socket.on('open', this.#onOpen)
      this.#socket.on('close', this.#onClose)
      this.#socket.on('event', this.#onEvent)
      this.#socket.on('error', this.#onError)
    }
  }

  #generateUrl = ({ url, api }) => {
    const query = Object.keys(this.#headers).map(key => `${key}=${this.#headers[key]}`).join('&')

    // The server may not always specify the FQDN of the socket server
    // and may return a path relative to the API URL.
    // We resolve the value sent by the server against the currently configured
    // API to create an URL with a full domain name included.
    // Note: this will likely result in an URL that ends in a slash
    const resolved = new URL(url, api).toString()

    // The browser APIs require a wss:// protocol for connecting websockets
    // so we'll do a simple replacement of http(s) -> ws(s)
    const wsUrl = resolved.replace('https://', 'wss://').replace('http://', 'ws://')

    // Resolve the sockets path against the URL we've generated for the socket
    // server so far.
    // Note: we unofficially support socket paths that include a base path,
    //       e.g. https://example.com/cobrowse/ so this means we should leave
    //       off the leading / so it resolves relative to the current path
    return new URL(`sockets/1/ws?${query}`, wsUrl).toString()
  }

  #generateProtocols = ({ token }) => {
    return [`cbio.http.headers.authorization.bearer.${toBase64(token).replaceAll('=', '')}`]
  }

  #onOpen = () => {
    // resubscribe tokens on successful (re)connections.
    [...this.#subscriptions].forEach(tokenFn => {
      const token = tokenFn()
      if (token) this.send('subscribe', token)
    })
    this.emit('open')
  }

  #onEvent = (event, data) => {
    this.emit('event', event, data)
  }

  #onError = (e) => {
    this.emit('error', e)
  }

  #onClose = () => {
    this.emit('close')
  }

  send = (event, data) => {
    if (!this.#socket) return false
    return this.#socket.send(event, data)
  }

  disconnect = () => {
    if (this.#socket) {
      this.#socket.close()
      this.#socket = null
    }
  }

  get latency () {
    return this.#socket ? Math.round(this.#socket.metrics.latency()) : undefined
  }

  subscribe = (subscriptionTokenFn) => {
    this.#subscriptions.add(subscriptionTokenFn)

    // ensure a connection has been attempted
    this.connect().catch(this.#onError)

    if (this.#socket && subscriptionTokenFn()) {
      this.send('subscribe', subscriptionTokenFn())
    }
  }

  unsubscribe = (tokenFn) => {
    this.#subscriptions.delete(tokenFn)
    if (this.#subscriptions.size === 0) this.disconnect()
  }
}
